import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { StoresCatalogService } from 'src/app/stores/store-catalog/stores-catalog.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

export interface ManageItemsModalConfig {
  title: string;
  items?: ManageItem[]; // Optional - will be fetched if not provided
  selectedItemIds: number[];
  selectionMode: 'single' | 'multiple';
  searchPlaceholder?: string;
  emptyMessage?: string;
  itemType: 'offer' | 'option' | 'category' | 'itemGroup';
  // For API fetching
  storeId?: number;
  catalogId?: number;
  categoryId?: number;
  languageId?: string; // Language ID from store settings
  // Callbacks for immediate link/unlink - now receives current selected IDs
  onLinkItem?: (
    itemId: number,
    currentSelectedIds: number[],
  ) => Observable<any>;
  onUnlinkItem?: (
    itemId: number,
    currentSelectedIds: number[],
  ) => Observable<any>;
}

export interface ManageItem {
  id: number;
  name: string;
  description?: string;
  imageUrl?: string;
  price?: number;
  isAvailable?: boolean;
  additionalInfo?: string;
}

export interface ManageItemsModalResult {
  selectedIds: number[];
  action: 'save' | 'cancel';
}

@Component({
  selector: 'app-manage-items-modal',
  templateUrl: './manage-items-modal.component.html',
  styleUrls: ['./manage-items-modal.component.scss'],
})
export class ManageItemsModalComponent implements OnInit, OnDestroy {
  filteredItems: ManageItem[] = [];
  paginatedItems: ManageItem[] = [];
  searchText = '';
  selectedIds: Set<number>;
  loading = false;
  loadingItemIds: Set<number> = new Set(); // Track which items are being processed

  // Server-side pagination
  currentPage = 0;
  pageSize = 20;
  totalElements = 0;
  totalPages = 0;

  // Search debouncing
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ManageItemsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManageItemsModalConfig,
    private catalogService: StoresCatalogService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {
    this.selectedIds = new Set(data.selectedItemIds || []);
  }

  ngOnInit() {
    // Setup search debouncing
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.performSearch();
      });

    if (this.data.items && this.data.items.length > 0) {
      // Use provided items (client-side pagination)
      this.filteredItems = [...this.data.items];
      this.totalElements = this.filteredItems.length;
      this.totalPages = Math.ceil(this.totalElements / this.pageSize);
      this.updatePaginatedItems();
    } else if (this.data.storeId) {
      // Fetch items from API (server-side pagination)
      this.loadItemsFromAPI();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadItemsFromAPI() {
    this.loading = true;

    if (this.data.itemType === 'offer') {
      // Get search text if it's at least 3 characters
      const searchText = this.searchText.trim();
      const searchParam = searchText.length >= 3 ? searchText : undefined;

      // Fetch offers with server-side pagination
      this.catalogService
        .loadOffersByLevel(
          this.data.catalogId,
          this.data.storeId,
          'PARENT',
          this.data.languageId || 'en', // Use languageId from config or fallback to 'en'
          this.currentPage,
          this.pageSize,
          'id,desc',
          searchParam,
        )
        .subscribe(
          (response) => {
            // Handle paginated response
            if (response.content) {
              this.paginatedItems = response.content.map((offer) => ({
                id: offer.offerId,
                name: offer.name,
                description: offer.aliasName || '',
              }));
              this.totalElements = response.totalElements || 0;
              this.totalPages = response.totalPages || 0;
            } else {
              // Handle array response (non-paginated)
              this.paginatedItems = response.map((offer) => ({
                id: offer.offerId,
                name: offer.name,
                description: offer.aliasName || '',
              }));
              this.totalElements = response.length;
              this.totalPages = 1;
            }

            this.loading = false;
          },
          (error) => {
            console.error('Error loading offers:', error);
            this.loading = false;
          },
        );
    } else if (this.data.itemType === 'itemGroup') {
      // Load item groups using categories API
      const searchText = this.searchText.trim();
      const searchParam = searchText.length >= 3 ? searchText : undefined;

      this.catalogService
        .loadCategoriesByLevelPaginated(
          this.data.catalogId,
          this.data.storeId,
          'ITEM_GROUP', // Use ITEM_GROUP level instead of PARENT
          this.data.languageId || 'en', // Use languageId from config or fallback to 'en'
          this.currentPage,
          this.pageSize,
          'position,asc',
          searchParam,
        )
        .subscribe(
          (response) => {
            // Handle paginated response
            if (response.content) {
              this.paginatedItems = response.content.map((category) => ({
                id: category.categoryId,
                name: category.name,
                description: category.shortDescription || '',
              }));
              this.totalElements = response.totalElements || 0;
              this.totalPages = response.totalPages || 0;
            } else {
              // Handle array response (non-paginated)
              this.paginatedItems = response.map((category) => ({
                id: category.categoryId,
                name: category.name,
                description: category.shortDescription || '',
              }));
              this.totalElements = response.length;
              this.totalPages = 1;
            }

            this.loading = false;
          },
          (error) => {
            console.error('Error loading item groups:', error);
            this.loading = false;
          },
        );
    }
    // Add other itemTypes here as needed
  }

  onSearch() {
    this.searchSubject.next(this.searchText);
  }

  private performSearch() {
    this.currentPage = 0; // Reset to first page on search

    if (this.data.items && this.data.items.length > 0) {
      // Client-side search
      const searchLower = this.searchText.toLowerCase().trim();
      // Only search if empty or at least 3 characters
      if (!searchLower) {
        this.filteredItems = [...this.data.items];
      } else if (searchLower.length < 3) {
        // Don't filter if less than 3 characters
        return;
      } else {
        this.filteredItems = this.data.items.filter(
          (item) =>
            item.name.toLowerCase().includes(searchLower) ||
            item.description?.toLowerCase().includes(searchLower),
        );
      }
      this.totalElements = this.filteredItems.length;
      this.totalPages = Math.ceil(this.totalElements / this.pageSize);
      this.updatePaginatedItems();
    } else {
      // Server-side search - reload from API
      const searchLower = this.searchText.toLowerCase().trim();
      // Only search if empty or at least 3 characters
      if (searchLower.length > 0 && searchLower.length < 3) {
        return;
      }
      this.loadItemsFromAPI();
    }
  }

  updatePaginatedItems() {
    if (this.data.items && this.data.items.length > 0) {
      // Client-side pagination
      const startIndex = this.currentPage * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      this.paginatedItems = this.filteredItems.slice(startIndex, endIndex);
    }
    // For server-side pagination, paginatedItems is already set by API response
  }

  toggleSelection(itemId: number) {
    // Prevent multiple clicks on the same item
    if (this.loadingItemIds.has(itemId)) {
      return;
    }

    const isCurrentlySelected = this.selectedIds.has(itemId);

    // If callbacks are provided, perform immediate link/unlink
    if (this.data.onLinkItem && this.data.onUnlinkItem) {
      if (isCurrentlySelected) {
        // First update the local state
        this.selectedIds.delete(itemId);
        // Get the current selected IDs after removal
        const currentSelectedIds = Array.from(this.selectedIds);

        // Unlink the item
        this.loadingItemIds.add(itemId);
        this.data.onUnlinkItem(itemId, currentSelectedIds).subscribe(
          () => {
            this.loadingItemIds.delete(itemId);
            this.toastr.success(
              this.translate.instant(
                'admin.store.catalog.offerUnlinkedSuccess',
              ),
            );
          },
          (error) => {
            // Revert the change on error
            this.selectedIds.add(itemId);
            this.loadingItemIds.delete(itemId);
            this.toastr.error(
              this.translate.instant('admin.store.catalog.failedToUnlinkOffer'),
            );
            console.error('Error unlinking offer:', error);
          },
        );
      } else {
        // First update the local state
        this.selectedIds.add(itemId);
        // Get the current selected IDs after addition
        const currentSelectedIds = Array.from(this.selectedIds);

        // Link the item
        this.loadingItemIds.add(itemId);
        this.data.onLinkItem(itemId, currentSelectedIds).subscribe(
          () => {
            this.loadingItemIds.delete(itemId);
            this.toastr.success(
              this.translate.instant('admin.store.catalog.offerLinkedSuccess'),
            );
          },
          (error) => {
            // Revert the change on error
            this.selectedIds.delete(itemId);
            this.loadingItemIds.delete(itemId);
            this.toastr.error(
              this.translate.instant('admin.store.catalog.failedToLinkOffer'),
            );
            console.error('Error linking offer:', error);
          },
        );
      }
    } else {
      // Fallback to old behavior if callbacks not provided
      if (this.data.selectionMode === 'single') {
        this.selectedIds.clear();
        this.selectedIds.add(itemId);
      } else {
        if (this.selectedIds.has(itemId)) {
          this.selectedIds.delete(itemId);
        } else {
          this.selectedIds.add(itemId);
        }
      }
    }
  }

  isItemLoading(itemId: number): boolean {
    return this.loadingItemIds.has(itemId);
  }

  isSelected(itemId: number): boolean {
    return this.selectedIds.has(itemId);
  }

  isAllSelected(): boolean {
    return (
      this.paginatedItems.length > 0 &&
      this.paginatedItems.every((item) => this.selectedIds.has(item.id))
    );
  }

  isSomeSelected(): boolean {
    return (
      this.paginatedItems.some((item) => this.selectedIds.has(item.id)) &&
      !this.isAllSelected()
    );
  }

  toggleSelectAll() {
    if (this.isAllSelected()) {
      // Deselect all on current page
      this.paginatedItems.forEach((item) => this.selectedIds.delete(item.id));
    } else {
      // Select all on current page
      this.paginatedItems.forEach((item) => this.selectedIds.add(item.id));
    }
  }

  onSave() {
    const result: ManageItemsModalResult = {
      selectedIds: Array.from(this.selectedIds),
      action: 'save',
    };
    this.dialogRef.close(result);
  }

  onCancel() {
    const result: ManageItemsModalResult = {
      selectedIds: [],
      action: 'cancel',
    };
    this.dialogRef.close(result);
  }

  getSelectedCount(): number {
    return this.selectedIds.size;
  }

  // Pagination methods
  getTotalPages(): number {
    return this.totalPages;
  }

  onPageChange(paging: { page: number; size: number }) {
    this.currentPage = paging.page; // Already 0-based from pager component
    if (this.data.items && this.data.items.length > 0) {
      this.updatePaginatedItems();
    } else {
      this.loadItemsFromAPI();
    }
  }

  getStartIndex(): number {
    return this.currentPage * this.pageSize;
  }

  getEndIndex(): number {
    return Math.min(this.getStartIndex() + this.pageSize, this.totalElements);
  }
}
